import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  DropdownItem,
  DropdownMenu,
  Image,
  Input,
  Loader,
  Modal,
  TextArea,
} from "semantic-ui-react";
import UserProfilePlaceholder, {
  LetteredAvatarImageUrl,
} from "../../../../../components/common/userProfilePlaceholder";
import { debounce } from "lodash";
import {
  ActionBtnIcon,
  ChatEmptyIcon,
  CloseIcon,
  DownloadIcon,
  PopelChatIcon,
} from "../../../../../../Assets/Icons/svg";
import { useSelector } from "react-redux";
import { selectTranslations } from "../../../../../config/i18n/slice";
import useSocket from "../../../../../../hooks/useSocket";
import EllipsisPopover from "../../../../../components/common/EllipsisPopover";
import {
  addPeopleToChat,
  getWorkOrderChatPeople,
} from "../../../../../config/api/chatApis";
import { DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { getActiveUsersByRole } from "../../../../../config/sharedApi/peopleApis";
import { toast } from "react-toastify";
import { useSocketContext } from "../../../../../../contexts/socketContext";
import EmptyState from "../../../../../components/common/EmptyState";

const Chat = ({ workOrderData }) => {
  const currentUserId = useSelector((state) => state?.auth?.user?.id || null);
  const t = useSelector(selectTranslations);
  const organisationId = useSelector((state) => {
    if (state?.auth?.user?.organisation) {
      return state.auth.user.organisation.id;
    } else {
      return null;
    }
  });

  const {
    onlineUsers,
    currentConversationId: activeConversationId,
  } = useSocketContext();

  const workOrderConversationId = workOrderData?.id || null;
  const [rawParticipants, setRawParticipants] = useState([]);
  const [assignModal, setAssignModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [participants, setParticipants] = useState([]);
  const [loadData, setloadData] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [options, setOptions] = useState({
    people: {
      initOptions: [],
      options: [],
    },
  });

  const messagesLabel =
      t?.workOrders?.chat?.messages ??
      t?.workOrders?.activityTab?.messages ??
      t?.common?.messages ??
      'messages';

  const peopleLabel =
      t?.workOrders?.chat?.people ??
      t?.workOrders?.activityTab?.people ??
      t?.common?.people ??
      'people';



  const role = useSelector((state) => {
    if (state?.auth?.user?.id) {
      return state.auth.user.role.type;
    } else {
      return null;
    }
  });

  const {
    isConnected,
    messages,
    sendMessage,
    startTyping,
    stopTyping,
    fetchMessages,
    joinConversation,
    page,
    isLoading,
    hasMore,
    typingUsers,
  } = useSocket();

  useEffect(() => {
    if (workOrderConversationId && isConnected) {
      joinConversation(workOrderConversationId);
    }
  }, [workOrderConversationId, isConnected, joinConversation]);

  const handleSearchChange = (searchQuery) => {
    const filteredOptions = options.people.initOptions.filter(
      (option) =>
        option.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setOptions((prevOptions) => ({
      ...prevOptions,
      people: {
        ...prevOptions.people,
        options: filteredOptions,
      },
    }));

    setSearchQuery(searchQuery);
  };

  const handleCheckboxChange = (assignee) => {
    setSelectedAssignees((prev) => {
      if (prev.includes(assignee.value)) {
        return prev.filter((id) => id !== assignee.value);
      } else {
        return [...prev, assignee.value];
      }
    });
  };

  const transformAssigneesAndPeopleToParticipants = (participantsData) => {
    const addedUserIds = new Set();
    const participantsNewData = [];
    const getProfileImageUrl = (profileImageData) => {
      if (profileImageData && profileImageData.profileImage) {
        return profileImageData.profileImage;
      }
      return null;
    };

    const addUserToParticipants = (userData) => {
      if (userData && userData.id) {
        if (!addedUserIds.has(userData.id)) {
          participantsNewData.push({
            id: userData.id,
            firstName: userData.firstName,
            lastName: userData.lastName,
            profileImageUrl: getProfileImageUrl(userData.profileImage),
            isOnline: onlineUsers?.includes(userData.id) ? true : false,
          });
          addedUserIds.add(userData.id);
        }
      }
    };

    if (participantsData?.length) {
      participantsData.forEach((person) => {
        addUserToParticipants({
          id: person.id,
          firstName: person.firstName,
          lastName: person.lastName,
          profileImage: person.imageUrl,
        });
      });
    }

    return participantsNewData;
  };

  const fetchPoople = useCallback(async () => {
    setloadData(true);
    const res = await getWorkOrderChatPeople(workOrderData?.id);

    setRawParticipants(res);
    setloadData(false);
  }, [onlineUsers]);

  useEffect(() => {
    if (rawParticipants?.length > 0) {
      const transformedData = transformAssigneesAndPeopleToParticipants(
        rawParticipants
      );
      setParticipants(transformedData);
    }
  }, [rawParticipants, onlineUsers]);

  const handleAddPeople = async () => {
    const fetchData = await getActiveUsersByRole(
      ["user", "limited_admin", "admin"],
      organisationId,

      workOrderData?.attributes?.location?.data?.id
    );

    setAssignModal(true);
    if (fetchData.data) {
      const the_options = fetchData.data?.map((el) => {
        const the_image = el.profileImage?.url
          ? el.profileImage?.url
          : LetteredAvatarImageUrl(el?.firstName + " " + el.lastName);

        return {
          key: el.id,
          value: el.id,
          text: el.firstName + " " + el.lastName,
          description: el?.email,
          image: { avatar: true, src: the_image, size: "tiny" },
        };
      });

      const filterData = the_options.filter(
        (e) => !participants.some((user) => Number(user.id) === Number(e.value))
      );

      setOptions((prevOptions) => ({
        ...prevOptions,
        people: {
          initOptions: filterData,
          options: filterData,
        },
      }));

      return the_options;
    }
  };

  useEffect(() => {
    fetchPoople();
  }, []);

  const chatMessagesRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const handleScroll = useCallback(
    debounce(() => {
      if (!chatMessagesRef.current || isLoading || !hasMore) return;

      const { scrollTop } = chatMessagesRef.current;

      const isNearTop = scrollTop < 80;
      if (isNearTop) {
        const prevHeight = chatMessagesRef.current.scrollHeight;

        fetchMessages(page + 1, activeConversationId).then(() => {
          const newHeight = chatMessagesRef.current.scrollHeight;
          chatMessagesRef.current.scrollTop = newHeight - prevHeight;
        });
      }
    }, 200),
    [isLoading, hasMore, messages.length]
  );

  const [newMessage, setNewMessage] = useState("");

  const handleTyping = () => {
    if (isConnected && newMessage?.length > 0) {
      startTyping(activeConversationId);
    }
  };

  const handleStopTyping = () => {
    stopTyping(activeConversationId);
  };

  useEffect(() => {
    if (isConnected && newMessage?.length > 0) {
      handleTyping();

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        handleStopTyping();
      }, 5000);
    } else if (newMessage?.length === 0) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [newMessage, isConnected, activeConversationId]);

  useEffect(() => {
    const chatContainer = chatMessagesRef.current;

    if (chatContainer) {
      chatContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatContainer) {
        chatContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isLoading, hasMore, messages.length]);

  useEffect(() => {
    const chatContainer = chatMessagesRef.current;
    if (chatContainer && messages.length > 0) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }, [messages]);

  const translate = (translations, keyPath, params = {}) => {
    const keys = keyPath.split(".");
    let value = translations;

    for (const key of keys) {
      if (value && value[key] !== undefined) {
        value = value[key];
      } else {
        return keyPath;
      }
    }

    if (typeof value === "string") {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        value = value.replace(`{{${paramKey}}}`, paramValue);
      });
    }

    return value;
  };

  const formatMessageTimestamp = (timestamp) => {
    const messageDate = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - messageDate) / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) {
      return t.workOrders.activityTab.justNow;
    } else if (diffMinutes < 60) {
      return translate(t, "workOrders.activityTab.minuteAgo", {
        count: diffMinutes,
      });
    } else if (diffHours < 24) {
      return translate(t, "workOrders.activityTab.hourAgo", {
        count: diffHours,
      });
    } else if (diffDays < 2) {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      if (
        messageDate.getDate() === yesterday.getDate() &&
        messageDate.getMonth() === yesterday.getMonth() &&
        messageDate.getFullYear() === yesterday.getFullYear()
      ) {
        return translate(t, "workOrders.activityTab.yesterdayAt", {
          time: messageDate.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        });
      }
      return translate(t, "workOrders.activityTab.dayAgo", { count: diffDays });
    } else {
      return translate(t, "workOrders.activityTab.dateFormat", {
        date: messageDate.toLocaleDateString([], {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
      });
    }
  };

  const handleAssignAdd = async () => {
    const res = await addPeopleToChat(workOrderData?.id, selectedAssignees);
    if (res.statusCode === 200) {
      fetchPoople();
      setAssignModal(false);
      setSelectedAssignees([]);
    } else {
      toast.error(t.workOrders.activityTab.addAssigneeError);
    }
  };

  const selectedLanguage = useSelector((state) => {
    if (state?.i18n?.lang) {
      return state.i18n.lang;
    } else {
      return "en";
    }
  });

  const checkValidUser = () => {
    if (
      ["cancelled", "completed"].includes(workOrderData?.attributes?.status)
    ) {
      return false;
    }
    if (participants.some((user) => user.id === currentUserId)) {
      return true;
    }

    return false;
  };

  const handleSendMessage = () => {
    if (newMessage.trim() && isConnected && activeConversationId) {
      const messageData = {
        text: newMessage,
        conversationId: activeConversationId,
      };
      sendMessage(messageData);
      handleStopTyping();
      setNewMessage("");
    } else if (!isConnected) {
      console.warn(t.workOrders.activityTab.canNotSend);
    }
  };

  const renderAssignees = (assigneeData) => {
    const totalLength = assigneeData?.length;
    let renderedAssignees = [];

    if (totalLength === 0) {
      return null;
    }

    const MAX_VISIBLE_AVATARS = 3;

    const displayableParticipants = assigneeData.slice(0, MAX_VISIBLE_AVATARS);
    const remainingParticipants = assigneeData.slice(MAX_VISIBLE_AVATARS);
    const additionalCount = remainingParticipants.length;

    renderedAssignees = displayableParticipants.map((person, index) => (
      <div key={person.id} className="avatar-wrapper">
        {person.profileImageUrl ? (
          <Image
            avatar
            src={person.profileImageUrl}
            alt={`${person.firstName} ${person.lastName}`}
            className={`header-avatar ${index !== 0 ? "overlapping" : ""}`}
            style={{ width: "34px", height: "34px", borderRadius: "50%" }}
          />
        ) : (
          <UserProfilePlaceholder
            name={`${person.firstName} ${person.lastName}`}
            width="34px"
            height="34px"
            className={`header-avatar ${index !== 0 ? "overlapping" : ""}`}
          />
        )}
        <span
          className={`presence-indicator ${
            person.isOnline ? "online" : "offline"
          }`}
        />
      </div>
    ));

    if (additionalCount > 0) {
      const popoverContent = (
        <div className="avatar-wrapper popover-assignees-content">
          {" "}
          {remainingParticipants.map((person, idx) => (
            <div
              key={person.id || `rest-${idx}`}
              className="popover-assignee-item"
            >
              {person.profileImageUrl ? (
                <Image
                  avatar
                  src={person.profileImageUrl}
                  alt={`${person.firstName} ${person.lastName}`}
                  style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "50%",
                    marginRight: "8px",
                  }}
                />
              ) : (
                <UserProfilePlaceholder
                  name={`${person.firstName} ${person.lastName}`}
                  width="34px"
                  height="34px"
                  style={{ marginRight: "8px" }}
                />
              )}
              <span
                className={`presence-indicator ${
                  person.isOnline ? "online" : "offline"
                }`}
                style={{ marginLeft: "auto" }}
              />
              <span className="popover-assignee-name">{`${person.firstName} ${person.lastName}`}</span>
            </div>
          ))}
        </div>
      );

      renderedAssignees.push(
        <div key="more" className="avatar-wrapper more-avatars-wrapper">
          <EllipsisPopover
            triggerMode="hover"
            delay={700}
            popoverContent={popoverContent}
          >
            <Image
              avatar
              src={`https://ui-avatars.com/api/?name=%2b${additionalCount}&background=E4E6EA&bold=true&color=788CA5`}
              alt="More"
              className="more-avatars"
              style={{ width: "34px", height: "34px", borderRadius: "50%" }}
            />
          </EllipsisPopover>
        </div>
      );
    }

    return <div className="avatars">{renderedAssignees}</div>;
  };
  const renderTypingAvatars = (allParticipants, typingUserIds = []) => {
    const typingIdSet = new Set(typingUserIds.map((id) => id));

    if (typingIdSet.size === 0) {
      return null;
    }

    const typingParticipants = allParticipants.filter((p) =>
      typingUserIds.includes(p.id)
    );

    if (typingParticipants.length === 0) {
      return null;
    }

    return (
      <div className="d-flex mb-2 typing-avatars-container">
        {typingParticipants.map((person) => (
          <div key={person.id} className="avatar-wrapper typing-avatar-item">
            {person.profileImageUrl ? (
              <Image
                avatar
                src={person.profileImageUrl}
                alt={`${person.firstName} ${person.lastName}`}
                className="header-avatar"
                style={{ width: "34px", height: "34px", borderRadius: "50%" }}
              />
            ) : (
              <UserProfilePlaceholder
                name={`${person.firstName} ${person.lastName}`}
                width="34px"
                height="34px"
                className="header-avatar"
                initials={person.initials}
              />
            )}
          </div>
        ))}

        <span className="typing-indicator-small">
          {t.workOrders.activityTab.typing}
        </span>
      </div>
    );
  };

  return (
    <>
      {!isConnected ? (
        <div className="text-center">
          {t.workOrders.activityTab.socketError}
        </div>
      ) : (
        <div className={`chat-container `}>
          <div className="chat-header">
            <div>{renderAssignees(participants)}</div>
            {["super_admin", "admin", "limited_admin"].includes(role) && (
              <div className="action-custom-menu-dropdown action-custom-menu-dropdown-2 ">
                <UncontrolledDropdown className="mr-2" direction="down">
                  <DropdownToggle
                    color="primary"
                    className="position-relative c-pointer option-toggle"
                  >
                    <ActionBtnIcon />
                  </DropdownToggle>
                  <DropdownMenu
                    className={selectedLanguage == "ar" ? "rtl" : ""}
                  >
                    <DropdownItem className="divider" />
                    <DropdownItem onClick={() => handleAddPeople()}>
                      <DownloadIcon /> {t.workOrders.activityTab.addPoeple}
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            )}
          </div>

          <div className="chat-messages" ref={chatMessagesRef}>
            {(isLoading && hasMore) || loadData ? (
              <Loader active inline="centered">
                {t.common.loading}
              </Loader>
            ) : (participants?.length === 0 && messages?.length === 0) ? (
              <EmptyState
                icon={<PopelChatIcon />}
                text={translate(t, "workOrders.activityTab.noPeopleAdded", {
                  highlight: `<span class="empty-state-highlight">${peopleLabel}</span>`,
                })}
                remainingText={t.workOrders.activityTab.addedToChat}
                highlight={t.people.title}
                buttonLabel={t.people.add_people}
                onButtonClick={
                  ["super_admin", "admin", "limited_admin"].includes(role)
                    ? () => handleAddPeople()
                    : null
                }
              />
            ) : messages?.length === 0 ? (
              <EmptyState
                icon={<ChatEmptyIcon />}
                text={translate(t, "workOrders.activityTab.noMessage", {
                  highlight: `<span class="empty-state-highlight">${messagesLabel}</span>`,
                })}
               
                subTitle={t.workOrders.activityTab.startConv}
              />
            ) : null}

            {messages.map((msg) => {
              const senderName = `${msg?.messageSender?.firstName
                ?.charAt(0)
                ?.toUpperCase() +
                msg?.messageSender?.firstName?.slice(
                  1
                )} ${msg?.messageSender?.lastName?.charAt(0)?.toUpperCase() +
                msg?.messageSender?.lastName?.slice(1)}`;
              const isSelf = msg.senderId === currentUserId;

              return (
                <React.Fragment key={msg.id}>
                  <div className={`chat-message-container `}>
                    <div
                      className={`avatar-container ${isSelf ? "reverse" : ""}`}
                    >
                      {msg.messageSender?.imageUrl ? (
                        <Image
                          avatar
                          src={msg.messageSender?.imageUrl}
                          alt={senderName}
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "50%",
                          }}
                        />
                      ) : (
                        <UserProfilePlaceholder
                          name={senderName}
                          width="34px"
                          height="34px"
                        />
                      )}
                      <div className="sender-name">{senderName}</div>
                    </div>

                    <div
                      className={`chat-message-content ${isSelf ? "self" : ""}`}
                    >
                      <>
                        <div className="chat-message-text">
                          {msg.textContent}
                        </div>
                        <div className="chat-message-timestamp">
                          {formatMessageTimestamp(msg.createdAt)}
                        </div>
                      </>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div />
          </div>
          <div className="position-relative">
            {renderTypingAvatars(participants, typingUsers)}
          </div>
          <div
            className={`chat-input ${
              !isConnected || !checkValidUser() ? "disable" : ""
            }`}
          >
            <div className="upload-container mt-5">
              <TextArea
                className={`upload-input ${!checkValidUser() ? "disable" : ""}`}
                disabled={!checkValidUser()}
                rows={2}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={
                  !isConnected || !checkValidUser()
                    ? t.workOrders.activityTab.notAllowed
                    : t.workOrders.activityTab.type
                }
              />
              <div className="upload-section text-end">
                <button
                  disabled={
                    !newMessage.trim() || !isConnected || !checkValidUser()
                  }
                  onClick={handleSendMessage}
                  className={`send-button ${
                    !checkValidUser() || !isConnected ? "disable" : ""
                  }`}
                >
                  {t.common.send}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        className="assets-modal add-assignee-modal"
        size="tiny"
        open={assignModal}
        onClose={() => {
          setAssignModal(false);
          setSelectedAssignees([]);
        }}
      >
        <div className="header-container">
          <span className="assets-header">
            {t.workOrders.activityTab.addPoeple}
          </span>
          <div>
            <span
              className="c-pointer text-right"
              onClick={() => {
                setAssignModal(false);

                setSelectedAssignees([]);
              }}
            >
              <CloseIcon />
            </span>
          </div>
        </div>

        <Modal.Content scrolling>
          <Input
            fluid
            className="mb-5"
            icon="search"
            placeholder={t.workOrders.activityTab.searchByEmail}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />

          {options.people?.options?.length > 0 ? (
            options.people.options.map((option) => (
              <div onClick={() => handleCheckboxChange(option)} key={option.key} className={`assignee-item d-flex align-items-center p-2 ${selectedAssignees?.includes(option.value) ? "selected" : ""}`}>
              
                <Image
                  avatar
                  src={option.image.src}
                  style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "50%",
                  }}
                />
                <div>
                  <div>
                    <b>{option.text}</b>
                  </div>
                  <div>{option.description}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 mt-5 text-center text-muted">
              {t.workOrders.activityTab.noPeopleFound}
            </div>
          )}
        </Modal.Content>

        <div
          className={`modal-button-container ${
            selectedLanguage == "en" ? "mr-6" : "ml-10"
          }`}
        >
          <button
            onClick={() => {
              setAssignModal(false);

              setSelectedAssignees([]);
            }}
            className="modal-form-action-button"
          >
            {t.assets.form.cancel}
          </button>

          <button
            onClick={() => handleAssignAdd()}
            className="modal-form-action-button light-blue"
          >
            {t.common.add}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Chat;
